import clsx from "clsx";
import {Pagination} from "swiper";
import React, {useContext, useMemo, useState} from "react";
import {Swiper, SwiperSlide} from "swiper/react";

import {buyNowContext} from "../BuyNow";
import PriceView from "../common/PriceView";
import {PlanChoice} from "./shapa-wp/PlanChoiceNew";
import {ScaleFeature} from "./shapa-wp/ScaleFeature";
import {PLAN_NAMES, PRODUCT_CATEGORIES} from "../../../../constants";
import PromoCode from "../common/PromoCode";
import LineItem from "../../utils/LineItem";
import { FreeScaleOffer } from "../common/FreeScaleOffer";

const FeatureContent = [{
  icon: "/img/buyNow/shapa-scale/motivating-missions.svg",
  featureTitle: "Motivating missions",
  featureDescription: "personalized toward your health & wellness goals"
}, {
  icon: "/img/buyNow/shapa-scale/shapa-scale.svg",
  featureTitle: "Anxiety-free measurements",
  featureDescription: "that eliminate distractions, enabling a clear vision to achieve your goals"
}, {
  icon: "/img/buyNow/shapa-scale/mindful-monitoring.svg",
  featureTitle: "Mindful monitoring",
  featureDescription: "of daily activity and eating habits for enhanced mental and physical well-being"
},];
const ProductImages = ["/img/buyNow/shapa-scale/product-image-1.jpg", "/img/buyNow/shapa-scale/product-image-2.jpg", "/img/buyNow/shapa-scale/product-image-3.jpg"];

const AsSeenLogos = ["/img/buyNow/shapa-scale/as-seen-1.svg", "/img/buyNow/shapa-scale/as-seen-2.svg", "/img/buyNow/shapa-scale/as-seen-3.svg",];

const bellowHeroLogos = [{
  icon: "/img/buyNow/shapa-scale/lifetime-warranty-badge.svg", line1: "Lifetime", line2: "Warranty", style: "d-flex"
}, {
  icon: "/img/buyNow/shapa-scale/money.svg", line1: "30 Day Money", line2: "Back Guarantee", style: "d-flex"
}, {
  icon: "/img/buyNow/shapa-scale/shipping-truck.svg",
  line1: "Free Shipping",
  line2: "Across USA",
  style: "d-none d-lg-flex"
}, {
  icon: "/img/buyNow/shapa-scale/fsa-card.svg",
  line1: "FSA Eligible",
  line2: "Please contact us at \nhsa@shapa.me to purchase \nusing your HSA account.",
  style: "d-none d-lg-flex",
  subTextStyle: "badge-sub-txt"
},];

const ShapaProductSelection = () => {
  const {
    lineItems,
    planProducts,
    scaleProducts,
    PURCHASE_STAGES,
    removeLineItems,
    setLineItems,
    setPurchaseStage,
    setLineItemsByCategory,
  } = useContext(buyNowContext);

  const { selectedScale, selectedPlan, yearlyPlan } = useMemo(() => {
    const scaleLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.SCALE);
    const planLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.MEMBERSHIP);
    const yearlyPlan = planProducts.find(plan => plan.name === PLAN_NAMES.YEARLY);
    const selectedScale = scaleLineItem ? scaleLineItem.product : null;
    const selectedPlan = planLineItem ? planLineItem.product : null;

    return { selectedScale, selectedPlan, yearlyPlan };
  }, [lineItems, planProducts]);
  const [alreadyHasScale, setAlreadyHasScale] = useState(!selectedScale);
  const [selectedProductImage, setSelectedProductImage] = useState(ProductImages[0]);

  const proceedToShippingHandle = () => {
    setPurchaseStage(PURCHASE_STAGES.SHIPPING);
  };

  const onCheckingAlreadyHaveScale = (event) => {
    setAlreadyHasScale(event.target.checked);

    // If the user already has a scale, we'll remove all line items and they can choose a plan.
    // If the user does not already have a scale, we need to explicitly set the scale/plan, because as of now, there are no choices for the user.
    const lineItems = [];
    if (!event.target.checked) {
      lineItems.push(new LineItem({product: scaleProducts[0], quantity: 1}));
      lineItems.push(new LineItem({product: planProducts.find(p => p.default), quantity: 1}));
    }

    setLineItems(lineItems);
  };

  return (<div>
    <section className="section-container">
      <div className={clsx("disabled-overlay", alreadyHasScale ? 'd-block' : 'd-none')}/>
      <div className="d-block d-lg-none mb-3">
        <Swiper
          loop
          slidesPerView={1}
          modules={[Pagination]}
          pagination={{clickable: true}}
          style={{
            "--swiper-pagination-color": "#22ACAC",
            "--swiper-pagination-bullet-inactive-color": "#D9D9D9",
            "--swiper-pagination-bullet-inactive-opacity": "1",
            "--swiper-pagination-bullet-size": "12px",
          }}
        >
          {ProductImages.map((logo) => (<SwiperSlide key={logo}>
            <div className="d-flex justify-content-center">
              <img alt={logo} src={logo} className="swiper-img-fluid"/>
            </div>
          </SwiperSlide>))}
        </Swiper>
        <FreeScaleOffer />
      </div>
      <div className="d-flex flex-row">
        <div className="d-none d-lg-flex product-preview-section">
          <div className="product-preview-options-section d-flex flex-column">
            {ProductImages
              .filter((image) => (image !== selectedProductImage))
              .map((image) => (<div key={image}>
                <img
                  src={image}
                  alt={image}
                  className="img-fluid"
                  onClick={() => {
                    setSelectedProductImage(image)
                  }}/>
              </div>))}
          </div>
          <div
            className="product-preview-selected-section d-flex flex-column justify-content-start align-items-center">
            <img alt={selectedProductImage} src={selectedProductImage} className="img-fluid product-preview"/>
            <div className="d-flex flex-column align-items-center">
              <span className="as-seen-text fw-bold text-center">AS SEEN ON:</span>
              <div className="product-as-seen-section d-flex justify-content-center">
                {AsSeenLogos.map((logo) => (<img key={logo} alt={logo} src={logo} className="img-fluid"/>))}
              </div>
            </div>
            <FreeScaleOffer />
          </div>
        </div>
        <div className="product-description-section d-flex justify-content-center">
          <div>
            <div className="d-none d-lg-flex">
              <img alt="starts" src="/img/buyNow/shapa-scale/starts.svg"/>
              <span className="review-text">Over 2,500 reviews</span>
            </div>
            <p className="headline fw-bold p-0">Numberless Scale</p>
            <div className="d-flex d-lg-none align-items-center">
              <img alt="starts" src="/img/buyNow/shapa-scale/starts.svg"/>
              <span className="review-text">Over 2,500 reviews</span>
            </div>

            <p className="headline-content fw-bolder">We help you develop good habits, track your health, and stay
              motivated without the
              anxiety of a
              traditional scale. Our numberless system helps you stay focused on developing healthy habits that
              naturally guide you to the body you've always wanted.</p>

            <div className="feature-container d-none d-lg-flex flex-column justify-content-center align-items-start">
              {FeatureContent.map(({icon, featureTitle, featureDescription}) => (
                <ScaleFeature key={icon} icon={icon} title={featureTitle} description={featureDescription}/>))}
            </div>

            <div className="d-flex justify-content-start mt-1 mt-lg-3">
              <p className={clsx("price-text p-0", alreadyHasScale ? 'strikethrough' : null)}>$99</p>
            </div>
            <div className={clsx("d-flex mt-3", alreadyHasScale ? 'z-index-over-disabled-overlay' : null)}>
              <input
                id="alreadyHaveScale"
                type="checkbox"
                defaultChecked={alreadyHasScale}
                style={{width: "1.4em", height: "1.4em", marginRight: "10px"}}
                onClick={event => onCheckingAlreadyHaveScale(event)}
              />
              <label className="already-have-scale-checkbox" htmlFor="alreadyHaveScale">I already have the Shapa
                scale</label>
            </div>
            <div className={clsx("flex-column", alreadyHasScale ? 'd-none' : 'd-flex')}
                 style={{marginTop: '26px', maxWidth: '550px'}}>
              <div className="d-flex flex-row justify-content-start align-items-center">
                <img style={{width: '34px', height: '37px', marginRight: '6px'}}
                     alt="gift"
                     src="/img/buyNow/shapa-scale/gift.svg"/>
                <p style={{
                  fontFamily: 'Inter', fontSize: "22px", fontWeight: 400, color: '#000000', padding: 0
                }}>
                  Enjoy your first 3 months on us.
                </p>
              </div>
              <p style={{
                fontFamily: 'Inter', fontSize: "11px", fontWeight: 400, color: '#6A6C77', padding: 0, marginLeft: '40px'
              }}>
                After 3 months, members will be billed ${yearlyPlan.price} annually for their membership. Members can alternatively opt
								into a monthly or lifetime subscription. Active membership is required for uninterrupted product access
								per member.
              </p>
            </div>
            <div
              className={clsx("flex-wrap justify-content-around mt-3", alreadyHasScale ? 'd-flex z-index-over-disabled-overlay' : 'd-none')}>
              {planProducts.filter(plan => !plan.isDelayedMonthlyPlan).map((plan) => {
                return (
                  <PlanChoice
                    key={plan.id}
                    plan={plan}
                    isSelected={plan === selectedPlan}
                    onSelected={plan => {
                      if (plan !== selectedPlan) {
                        setLineItemsByCategory([{product: plan, quantity: 1}]);
                      }
                    }}
                  />
                )
              })}
            </div>
            <div className="mt-3">
              <PromoCode className="text-center" specialButtonColor="#22ACAC"/>
            </div>
            <div
              className={clsx("d-flex flex-column flex-lg-row justify-content-around align-items-center mt-3", alreadyHasScale ? 'z-index-over-disabled-overlay' : null)}>
              <PriceView hideDecimalIfWhole={true} includeShippingCost={false} className="text-center price-view"/>
              <button
                className={clsx("cart-button mt-3 mt-lg-0 ms-0 ms-lg-4", alreadyHasScale && !selectedPlan ? 'cart-button-disabled' : null)}
                disabled={alreadyHasScale && !selectedPlan}
                onClick={proceedToShippingHandle}>
                ADD TO CART
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section className="section-container d-flex justify-content-around">
      {bellowHeroLogos.map(({icon, line1, line2, style, subTextStyle}) => (
        <div key={icon} style={{flex: 1}}
             className={clsx("d-flex flex-column justify-content-center align-items-center", style)}>
          <img alt={icon} src={icon} className="img-fluid pb-2"/>
          <div className="d-flex flex-column flex-wrap justify-content-center align-items-center">
            <p className="badge-txt p-0">
              {line1}
            </p>
            <p className={clsx("badge-txt p-0", subTextStyle)}>
              {line2}
            </p>
          </div>
        </div>))}
    </section>
  </div>)
};

export default ShapaProductSelection;
